// React and Router
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom"
import Select from 'react-select'; // Import react-select

// Firebase
import "firebase/auth"
import { auth } from "../../firebase.js"

// Components
import Header from "../shared/Header"
import WorkspaceCheckNoDisplay from "../team_workspaces/WorkspaceCheckNoDisplay";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { fetchWorkspaceSnapshots } from "../../utils/teamWorkspaceSnapshotUtils";

// Styles
import '@fortawesome/fontawesome-free/css/all.min.css';
import "../../css/HomePage.css"

const typingTexts = [
        'for the freelancer',
        'for the everyday user', 
        'for the small team with big ideas',
        'for the solo founder'
];

function HomePage() {
        const { enhancedRightClickEnabled, updateWorkspaceCache } = useQuickLinks();
        const { emailVerified } = useQuickLinks();
        const { emailVerifiedLoading } = useQuickLinks();

        const [data, setData] = useState(null);
        const [loading, setLoading] = useState(true); 
        const [loadingMessage, setLoadingMessage] = useState('Checking login status...');
        const [error, setError] = useState(null); 
        const navigate = useNavigate();
        const [loggedIn, setLoggedIn] = useState(false)
        const [hasWorkspace, setHasWorkspace] = useState(false)
        const [ownedWorkspaces, setOwnedWorkspaces] = useState([]);
        const [sharedWorkspaces, setSharedWorkspaces] = useState([]);
        const [showWorkspaceOverlay, setShowWorkspaceOverlay] = useState(false);
        const [showOwnedWorkspaceOverlay, setShowOwnedWorkspaceOverlay] = useState(false);
        const [showMaintenanceWarning, setShowMaintenanceWarning] = useState(true);
        const [selectedWorkspace, setSelectedWorkspace] = useState(null);
        const [currentUserUid, setCurrentUserUid] = useState("");
        const [showWhatWeOfferModal, setShowWhatWeOfferModal] = useState(false);
        const [showDownloadModal, setShowDownloadModal] = useState(false);
        
        // Typing animation states
        const [currentTextIndex, setCurrentTextIndex] = useState(0);
        const [currentText, setCurrentText] = useState('');
        const [isDeleting, setIsDeleting] = useState(false);
        const [typingSpeed, setTypingSpeed] = useState(100); // Faster initial speed
        
        // Storage states
        const [storageInfo, setStorageInfo] = useState(null);
        const [storageLoading, setStorageLoading] = useState(false);
        
        const loggedOutOptions = [
                {
                        label: "Login",
                        icon: <i className="fas fa-sign-in-alt"></i>,
                        url: "/login",
                        onClick: () => navigate("/login")
                },
                {
                        label: "Register",
                        icon: <i className="fas fa-user-plus"></i>,
                        url: "/register",
                        onClick: () => navigate("/register")
                },
                {
                        label: "Try My Notebook",
                        icon: <i className="fas fa-book"></i>,
                        url: "/my-notebook",
                        onClick: () => navigate("/my-notebook")
                },
                {
                        label: "Visit YouTube Channel",
                        icon: <i className="fab fa-youtube"></i>,
                        url: "https://www.youtube.com/@schoolofintology4062",
                        onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer")
                },
                {
                        label: "Join Discord",
                        icon: <i className="fab fa-discord"></i>,
                        url: "https://discord.gg/DYkESHwgVA",
                        onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer")
                }
        ];

                // Context menu setup
                const contextMenuOptions = [
                        {
                                label: "Your Documents",
                                url: "/documentation",
                                onClick: () => navigate("/documentation"),
                        },
                        {
                                label: "Your Walkthroughs",
                                url: "/walkthroughs",
                                onClick: () => navigate("/walkthroughs")
                        },
                        {
                                label: "Your Notebook",
                                url: "/my-notebook",
                                onClick: () => navigate("/my-notebook")
                        },
                        {
                                label: "Your Workspace",
                                url: "/workspaces",
                                onClick: () => navigate("/workspaces")
                        },
                        {
                                label: "Account Settings",
                                url: `/account/${currentUserUid}`,
                                onClick: () => navigate(`/account/${currentUserUid}`)
                        }
                ];

                // Use loggedOutOptions if not logged in, otherwise contextMenuOptions
                const menuOptions = loggedIn ? contextMenuOptions : loggedOutOptions;
                const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

        const handleCloseMaintenanceWarning = () => {
                setShowMaintenanceWarning(false);
                // Optional: Store in localStorage to remember user's choice for this session
                localStorage.setItem('maintenanceWarningDismissed', 'true');
        }

        // Optional: Check if user previously dismissed the warning
        useEffect(() => {
                const dismissed = localStorage.getItem('maintenanceWarningDismissed');
                if (dismissed === 'true') {
                        setShowMaintenanceWarning(false);
                }
        }, []);

        // Background prefetch of workspace details (non-blocking)
        const prefetchWorkspaceDetails = useCallback(async (workspaceId) => {
                try {
                        const user = auth.currentUser;
                        if (!user) {
                                return;
                        }
                        
                        const token = await user.getIdToken();
                        
                        // Fetch workspace details
                        const detailsResponse = await fetch(
                                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/`,
                                {
                                        method: "GET",
                                        headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `${token}`,
                                                'Auth-Token': `Bearer ${token}`,
                                                'Requesting-User-Uid': user.uid
                                        }
                                }
                        );
                        
                        if (!detailsResponse.ok) {
                                return;
                        }
                        const workspaceDetails = await detailsResponse.json();
                        
                        // Fetch all data in parallel (including sidebar data)
                        const [pinnedRes, favoritesRes, mostViewedDocsRes, mostViewedSpacesRes, snapshotsData, spacesRes, pagesRes, walkthroughsRes] = await Promise.allSettled([
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pinned-documents/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/user-favorites/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/most-viewed-documents/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/most-viewed-spaces/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetchWorkspaceSnapshots(workspaceId),
                                // Sidebar data
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                }),
                                fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/walkthroughs/`, {
                                        headers: { 'Authorization': `${token}`, 'Auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid }
                                })
                        ]);
                        
                        // Parse successful responses
                        const getData = async (result) => {
                                if (result.status === 'fulfilled' && result.value) {
                                        if (result.value.ok) {
                                                return await result.value.json();
                                        }
                                        if (Array.isArray(result.value)) {
                                                return result.value;
                                        }
                                }
                                return null;
                        };
                        
                        const [pinnedDocs, favorites, mostViewedDocs, mostViewedSpaces, snapshots, spaces, pages, walkthroughs] = await Promise.all([
                                getData(pinnedRes),
                                getData(favoritesRes),
                                getData(mostViewedDocsRes),
                                getData(mostViewedSpacesRes),
                                Promise.resolve(snapshotsData.status === 'fulfilled' ? snapshotsData.value : null),
                                getData(spacesRes),
                                getData(pagesRes),
                                getData(walkthroughsRes)
                        ]);

                        // Cache all the data
                        updateWorkspaceCache({
                                workspaceDetails,
                                pinnedDocuments: pinnedDocs,
                                userFavorites: favorites,
                                mostViewedDocuments: mostViewedDocs?.most_viewed_documents ?? mostViewedDocs,
                                mostViewedSpaces: mostViewedSpaces?.most_viewed_spaces ?? mostViewedSpaces,
                                snapshots: snapshots,
                                userNames: null, // Will be fetched by TeamWorkspaceMainPage if needed
                                // Sidebar data
                                spaces: spaces,
                                pages: pages,
                                walkthroughs: walkthroughs
                        });
                        
                        console.debug('Workspace data prefetched and cached');
                } catch (error) {
                        // Silently fail - this is just optimization
                        console.debug('Workspace prefetch failed:', error);
                }
        }, [updateWorkspaceCache]);

        // Modified handler to receive workspace existence and properly identify owner workspace
        const handleWorkspaceCheck = useCallback((workspaceExists, name, isOwner, allWorkspaces) => {
                setHasWorkspace(workspaceExists);
                const owned = allWorkspaces ? allWorkspaces.filter(workspace =>
                        workspace.role === "OWNER"
                ) : [];
                setOwnedWorkspaces(owned);
                // Filter out workspaces where the user is not an owner (for shared workspaces)
                const shared = allWorkspaces ? allWorkspaces.filter(workspace =>
                        workspace.role !== "OWNER"
                ) : [];
                // Remove duplicate workspaces from shared list by creating a Map using workspace ID
                const uniqueShared = Array.from(
                        new Map(shared.map(item => [item.id, item])).values()
                );
                setSharedWorkspaces(uniqueShared);

                // Background: prefetch detail data for the first owned workspace.
                // The cache is single-slot today; richer multi-workspace caching is Phase 2.
                if (owned.length > 0 && owned[0].id) {
                        prefetchWorkspaceDetails(owned[0].id);
                }

                // Set loading to false after workspace check completes for logged-in users
                setLoading(false);
        }, [prefetchWorkspaceDetails]); // Add prefetchWorkspaceDetails as dependency

        // Theme cleanup on unmount
        const navigateToSocialMediaPage = (url) => () => {
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                console.warn("Invalid URL");
            }
        };

        useEffect(() => {
                setLoading(true);
                setLoadingMessage('Checking login status...');
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (user) {
                                setLoggedIn(true);
                                setCurrentUserUid(user.uid);
                                setLoadingMessage('Fetching workspace data...');
                                // Do not setLoading(false) here for logged-in users; wait for workspace check
                        } else {
                                setLoggedIn(false);
                                setLoading(false);
                        }
                });
                return () => { unsubscribe(); }
        }, []);

        // Typing animation effect
        useEffect(() => {
                const handleTyping = () => {
                        const fullText = typingTexts[currentTextIndex];
                        
                        if (!isDeleting) {
                                // Typing forward
                                if (currentText.length < fullText.length) {
                                        setCurrentText(fullText.substring(0, currentText.length + 1));
                                        setTypingSpeed(100); // Faster typing (was 150)
                                } else {
                                        // Finished typing, wait longer then start deleting
                                        setTimeout(() => setIsDeleting(true), 3500); // Longer pause (was 2000)
                                }
                        } else {
                                // Deleting
                                if (currentText.length > 0) {
                                        setCurrentText(fullText.substring(0, currentText.length - 1));
                                        setTypingSpeed(50); // Faster deleting (was 75)
                                } else {
                                        // Finished deleting, move to next text
                                        setIsDeleting(false);
                                        setCurrentTextIndex((prev) => (prev + 1) % typingTexts.length);
                                        setTypingSpeed(100); // Faster initial speed (was 150)
                                }
                        }
                };

                const timer = setTimeout(handleTyping, typingSpeed);
                return () => clearTimeout(timer);
        }, [currentText, currentTextIndex, isDeleting, typingSpeed]); // Removed typingTexts since it's now a constant

        const handleDivRedirect = () => {
                if (loggedIn) {
                        navigate("/create");
                } else {
                        navigate("/register")
                }
        }

        const handleWalkthroughDivRedirect = () => {
                navigate("/walkthroughs/create")
        }

        const handleYouTubeChannelRedirect = () => {
                window.open("https://www.youtube.com/@schoolofintology4062", "_blank");
        }

        const handleTrialNotebookRedirect = () => {
                navigate("/my-notebook");
        }

        const handleImportDocumentRedirect = () => {
                navigate("/import-document");
        }

        const handleImportSpaceRedirect = () => {
                navigate("/import-space");
        }

        const handleSharedWorkspaceClick = () => {
                setShowWorkspaceOverlay(true);
        }

        const handleCloseOverlay = () => {
                setShowWorkspaceOverlay(false);
                setSelectedWorkspace(null);
        }

        const handleWorkspaceSelection = (workspace) => {
                if (!workspace) return;

                // Navigate to the specific workspace using the custom domain
                navigate(`/workspace/${workspace.custom_domain}`);
                setShowWorkspaceOverlay(false);
        }

        const handleOwnedWorkspaceClick = () => {
                if (ownedWorkspaces.length === 1) {
                        navigate("/workspaces");
                        return;
                }
                setShowOwnedWorkspaceOverlay(true);
        }

        const handleCloseOwnedOverlay = () => {
                setShowOwnedWorkspaceOverlay(false);
                setSelectedWorkspace(null);
        }

        const handleOwnedWorkspaceSelection = (workspace) => {
                if (!workspace) return;
                navigate(`/workspace/${workspace.custom_domain}`);
                setShowOwnedWorkspaceOverlay(false);
        }

        const handleShowWhatWeOffer = () => {
                setShowWhatWeOfferModal(true);
        }

        const handleCloseWhatWeOffer = () => {
                setShowWhatWeOfferModal(false);
        }

        const handleShowDownloadModal = () => {
                setShowDownloadModal(true);
        }

        const handleCloseDownloadModal = () => {
                setShowDownloadModal(false);
        }

        // Fetch storage info in background (non-blocking)
        const fetchStorageInfo = useCallback(async (uid) => {
                if (!uid) return;
                setStorageLoading(true);
                try {
                        // Get Firebase Auth token
                        const user = auth.currentUser;
                        if (!user) {
                                console.error('No current user for token');
                                setStorageLoading(false);
                                return;
                        }
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/storage/`, {
                                method: 'GET',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `${token}`,
                                }
                        });

                        if (response.ok) {
                                const data = await response.json();
                                setStorageInfo(data.storage_info);
                        } else {
                                console.error('Failed to fetch storage info');
                        }
                } catch (error) {
                        console.error('Error fetching storage info:', error);
                } finally {
                        setStorageLoading(false);
                }
        }, []);

        // Fetch storage info when user is logged in (runs in background)
        useEffect(() => {
                if (loggedIn && currentUserUid) {
                        fetchStorageInfo(currentUserUid);
                }
        }, [loggedIn, currentUserUid, fetchStorageInfo]);

        // Helper function to format bytes to readable format
        const formatBytes = (bytes) => {
                if (bytes === 0) return '0 B';
                const k = 1024;
                const sizes = ['B', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
        };

        // Convert shared workspaces to options for the Select component
        const workspaceOptions = sharedWorkspaces.map(workspace => ({
                label: workspace.name,
                value: workspace.id,
                // Including custom_domain isn't strictly necessary here since we look up the
                // full workspace object, but it could be useful for debugging
                custom_domain: workspace.custom_domain
        }));

        if (loading) {
            return (
                <>
                    {loggedIn && <WorkspaceCheckNoDisplay onWorkspaceCheck={handleWorkspaceCheck} />}
                    <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                        <div className="coffee-cup-loader">
                            <div className="cup">
                                <div className="cup-body"></div>
                                <div className="cup-handle"></div>
                                <div className="cup-base"></div>
                                <div className="cup-shadow"></div>
                                <div className="steam steam1"></div>
                                <div className="steam steam2"></div>
                                <div className="steam steam3"></div>
                            </div>
                        </div>
                        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                    </div>
                </>
            );
        }

        return (
                <div 
                        className="container-fluid" 
                        style={{ padding: "0", overflow: "hidden" }}
                        onContextMenu={enhancedRightClickEnabled ? handleContextMenu : undefined}
                >

                        {/* Maintenance Warning */}
                        {/* {showMaintenanceWarning && (
                                <div className="HomePage__maintenance-warning">
                                        <div className="HomePage__maintenance-warning-content">
                                                <i className="fas fa-exclamation-triangle HomePage__maintenance-warning-icon"></i>
                                                <p className="HomePage__maintenance-warning-text">
                                                        <strong>Scheduled Maintenance:</strong> Bluebird Documentation is currently under maintenance. Expected downtime is 1 hour. We apologize for any inconvenience.
                                                </p>
                                        </div>
                                        <button 
                                                className="HomePage__maintenance-warning-close"
                                                onClick={handleCloseMaintenanceWarning}
                                                aria-label="Close maintenance warning"
                                        >
                                                <i className="fas fa-times"></i>
                                        </button>
                                </div>
                        )} */}

                        {/* Email verification banner - only show when not loading and email is not verified */}
                        {!emailVerifiedLoading && !emailVerified && loggedIn && (
                                <div className="HomePage__verify-email-div">
                                        <p className="small" style={{ marginBottom: "0px" }}>
                                                Please <span
                                                        style={{ textDecoration: "underline", cursor: "pointer", color: "#fff" }}
                                                        onClick={() => navigate("/verify-email")}
                                                        tabIndex={0}
                                                        role="button"
                                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate("/verify-email"); }}
                                                >
                                                        verify your email
                                                </span> to unlock all features.
                                        </p>
                                </div>
                        )}

                        <div style={{ background: "var(--bb-shell-bg)" }}>
                                <Header />
                        </div>
                        <div className="notification"></div>
                        <div className="row HomePage__main-container">
                                <div className="col-12 d-flex flex-column justify-content-center align-items-center">
                                        {loggedIn ? (
                                                <>
                                                        <h1 className="large HomePage__text"><span className="HomePage__text-alternate">Bluebird</span> Documentation</h1>
                                                        <p className="large HomePage__text">
                                                                Documentation made simple, free, and <span className="HomePage__text-alternate">{currentText}</span>
                                                                <span className="HomePage__typing-cursor">|</span>
                                                        </p>
                                                        <div className="HomePage__middle-divs">
                                                                <div className="HomePage__create-container-small" onClick={handleDivRedirect}>
                                                                        <h5 className="light-alt HomePage__create-div-text">Documentation</h5>
                                                                        <i className="fa-solid fa-file HomePage__icon-mobile"></i>
                                                                        <i className="fas fa-plus HomePage__plus-sign-small"></i>
                                                                </div>
                                                                <div className="HomePage__create-container-small" onClick={handleWalkthroughDivRedirect}>
                                                                        <h5 className="light-alt HomePage__create-div-text">Walkthroughs</h5>
                                                                        <i className="fa-solid fa-book-open HomePage__icon-mobile"></i>
                                                                        <i className="fas fa-plus HomePage__plus-sign-small"></i>
                                                                </div>
                                                                
                                                                {/* Show for workspace owners */}
                                                                {ownedWorkspaces.length > 0 && (
                                                                        <div className="HomePage__workspace-container" onClick={handleOwnedWorkspaceClick}>
                                                                                <h5 className="light-alt HomePage__create-div-text">
                                                                                        {ownedWorkspaces.length === 1
                                                                                                ? ownedWorkspaces[0].name
                                                                                                : "My Workspaces"}
                                                                                </h5>
                                                                                <i className="fa-solid fa-building HomePage__icon-mobile"></i>
                                                                        </div>
                                                                )}
                                                                
                                                                {/* Show for users who only have shared access */}
                                                                {hasWorkspace && sharedWorkspaces.length > 0 && (
                                                                        <div className="HomePage__workspace-container" onClick={handleSharedWorkspaceClick}>
                                                                                <h5 className="light-alt HomePage__create-div-text">
                                                                                        Shared Workspaces
                                                                                </h5>
                                                                                <i className="fa-solid fa-list HomePage__icon-mobile"></i>
                                                                        </div>
                                                                )}
                                                        </div>
                                                        <div className="HomePage__bottom-icons" onClick={navigateToSocialMediaPage("https://discord.gg/DYkESHwgVA")}>
                                                            <p className="extra-small">Join our Discord!</p><i className="fa-brands fa-discord"></i>
                                                        </div>

                                                        {/* Storage Display - Bottom Right */}
                                                        {storageInfo && (
                                                                <div className="HomePage__storage-display">
                                                                        <svg className="HomePage__storage-circle" viewBox="0 0 100 100">
                                                                                {/* Background circle */}
                                                                                <circle
                                                                                        cx="50"
                                                                                        cy="50"
                                                                                        r="45"
                                                                                        fill="none"
                                                                                        stroke="rgba(91, 146, 229, 0.2)"
                                                                                        strokeWidth="8"
                                                                                />
                                                                                {/* Progress circle */}
                                                                                <circle
                                                                                        cx="50"
                                                                                        cy="50"
                                                                                        r="45"
                                                                                        fill="none"
                                                                                        stroke="#5B92E5"
                                                                                        strokeWidth="8"
                                                                                        strokeLinecap="round"
                                                                                        strokeDasharray={`${2 * Math.PI * 45}`}
                                                                                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - storageInfo.storage_used_percentage / 100)}`}
                                                                                        transform="rotate(-90 50 50)"
                                                                                        className="HomePage__storage-progress"
                                                                                />
                                                                        </svg>
                                                                        <div className="HomePage__storage-text">
                                                                                <div className="HomePage__storage-percentage">
                                                                                        {Math.round(storageInfo.storage_used_percentage)}%
                                                                                </div>
                                                                                <div className="HomePage__storage-label">
                                                                                        {formatBytes(storageInfo.storage_used_bytes)} / {formatBytes(storageInfo.storage_limit_bytes)}
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        )}

                                                        {/* What We Offer / Download Section */}
                                                        <div className="HomePage__import-section">
                                                                <div className="HomePage__import-section-buttons">
                                                                        <div className="HomePage__what-we-offer-trigger" onClick={handleShowWhatWeOffer}>
                                                                                <i className="fas fa-info-circle"></i>
                                                                                <span>What we offer</span>
                                                                        </div>
                                                                        <div className="HomePage__download-trigger" onClick={handleShowDownloadModal}>
                                                                                <i className="fas fa-download"></i>
                                                                                <span>Download Focus</span>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </>
                                        ) : (
                                                <>
                                                        <p className="large HomePage__text-info">Welcome to your new home for all your documents!</p>
                                                        <p className="large HomePage__text-info2">Where community and documentation meet.</p>
                                                        <h1 className="large HomePage__text-register"><span className="HomePage__text-alternate-register">Bluebird</span> Documentation</h1>
                                                        <p className="large HomePage__text-register">
                                                                Documentation made simple, free, and <span className="HomePage__text-alternate-register">{currentText}</span>
                                                                <span className="HomePage__typing-cursor">|</span>
                                                        </p>
                                                        <div className="HomePage__middle-divs">
                                                                <div className="HomePage__create-container-register" onClick={handleDivRedirect}>
                                                                        <h3 className="light" style={{ color: "#090e16" }}>Register</h3>
                                                                        <i className="fa-solid fa-user-plus HomePage__register-icon"></i>
                                                                </div>
                                                                <div className="HomePage__create-container-register" onClick={handleTrialNotebookRedirect}>
                                                                        <h3 className="light" style={{ color: "#090e16" }}>Try My Notebook</h3>
                                                                        <i className="fa-solid fa-book HomePage__register-icon"></i>
                                                                </div>
                                                                <div className="HomePage__create-container-register-youtube" onClick={handleYouTubeChannelRedirect}>
                                                                        <p className="small" style={{ color: "white" }}>Visit our YouTube channel to learn more!</p>
                                                                        <i style={{ color: "white" }} className="fa-brands fa-youtube HomePage__register-icon"></i>
                                                                </div>
                                                        </div>
                                                        <div className="HomePage__bottom-icons" onClick={navigateToSocialMediaPage("https://discord.gg/DYkESHwgVA")}>
                                                            <p className="extra-small">Join our Discord!</p><i className="fa-brands fa-discord"></i>
                                                        </div>

                                                        {/* What We Offer / Download Section */}
                                                        <div className="HomePage__import-section">
                                                                <div className="HomePage__import-section-buttons">
                                                                        <div className="HomePage__what-we-offer-trigger" onClick={handleShowWhatWeOffer}>
                                                                                <i className="fas fa-info-circle"></i>
                                                                                <span>What we offer</span>
                                                                        </div>
                                                                        <div className="HomePage__download-trigger" onClick={handleShowDownloadModal}>
                                                                                <i className="fas fa-download"></i>
                                                                                <span>Download Focus</span>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </>
                                        )}
                                </div>
                        </div>

                        {/* Shared Workspace Overlay */}
                        {showWorkspaceOverlay && (
                                <div className="HomePage__workspace-overlay" onClick={handleCloseOverlay}>
                                        <div className="HomePage__workspace-overlay-content" onClick={e => e.stopPropagation()}>
                                                <div className="HomePage__workspace-overlay-header">
                                                        <h3>Select a Shared Workspace</h3>
                                                        <button className="HomePage__workspace-overlay-close" onClick={handleCloseOverlay}>
                                                                <i className="fas fa-times"></i>
                                                        </button>
                                                </div>
                                                
                                                <div className="HomePage__workspace-search">
                                                        <Select
                                                                options={workspaceOptions}
                                                                value={workspaceOptions.find(option => option.value === selectedWorkspace)}
                                                                onChange={(option) => handleWorkspaceSelection(option ? option : null)}
                                                                placeholder="Search workspaces..."
                                                                isSearchable={true}
                                                                className="HomePage__workspace-select"
                                                                styles={{
                                                                        // Style the control (the input container)
                                                                        control: (provided, state) => ({
                                                                                ...provided,
                                                                                backgroundColor: 'var(--bb-shell-bg)',
                                                                                borderColor: state.isFocused ? 'var(--bb-accent)' : 'var(--bb-shell-text)',
                                                                                boxShadow: state.isFocused ? '0 0 0 1px var(--bb-accent)' : 'none',
                                                                                '&:hover': {
                                                                                        borderColor: 'var(--bb-accent)'
                                                                                }
                                                                        }),
                                                                        // Style the menu (dropdown container)
                                                                        menu: (provided) => ({
                                                                                ...provided,
                                                                                backgroundColor: 'var(--bb-shell-bg)',
                                                                                border: '1px solid var(--bb-accent)',
                                                                                boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)'
                                                                        }),
                                                                        // Style the options
                                                                        option: (provided, state) => ({
                                                                                ...provided,
                                                                                backgroundColor: state.isSelected 
                                                                                        ? 'var(--bb-accent)' 
                                                                                        : state.isFocused 
                                                                                                ? 'rgba(91, 146, 229, 0.2)' 
                                                                                                : 'var(--bb-shell-bg)',
                                                                                color: state.isSelected 
                                                                                        ? 'white' 
                                                                                        : 'var(--bb-shell-text)',
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                        backgroundColor: state.isSelected 
                                                                                                ? 'var(--bb-accent)' 
                                                                                                : 'rgba(91, 146, 229, 0.2)'
                                                                                }
                                                                        }),
                                                                        // Style the input
                                                                        input: (provided) => ({
                                                                                ...provided,
                                                                                color: 'var(--bb-shell-text)'
                                                                        }),
                                                                        // Style the placeholder
                                                                        placeholder: (provided) => ({
                                                                                ...provided,
                                                                                color: 'var(--bb-shell-text)'
                                                                        }),
                                                                        // Style the single value (selected option)
                                                                        singleValue: (provided) => ({
                                                                                ...provided,
                                                                                color: 'var(--bb-shell-text)'
                                                                        }),
                                                                        // Style the dropdown indicator (arrow)
                                                                        dropdownIndicator: (provided) => ({
                                                                                ...provided,
                                                                                color: 'var(--bb-shell-text)',
                                                                                '&:hover': {
                                                                                        color: 'var(--bb-accent)'
                                                                                }
                                                                        }),
                                                                        // Style the indicator separator
                                                                        indicatorSeparator: (provided) => ({
                                                                                ...provided,
                                                                                backgroundColor: 'var(--bb-shell-text)'
                                                                        })
                                                                }}
                                                        />                                                
                                                </div>
                                                
                                                <div className="HomePage__workspace-list">
                                                        <h4>Your Shared Workspaces</h4>
                                                        {sharedWorkspaces.map(workspace => (
                                                                <div
                                                                        key={workspace.id}
                                                                        className="HomePage__workspace-list-item"
                                                                        onClick={() => handleWorkspaceSelection(workspace)}
                                                                >
                                                                        <span>{workspace.name}</span>
                                                                        <span className="HomePage__workspace-role">{workspace.role}</span>
                                                                </div>
                                                        ))}
                                                </div>
                                        </div>
                                </div>
                        )}

                        {/* Owned Workspace Overlay (only when user owns more than one) */}
                        {showOwnedWorkspaceOverlay && (
                                <div className="HomePage__workspace-overlay" onClick={handleCloseOwnedOverlay}>
                                        <div className="HomePage__workspace-overlay-content" onClick={e => e.stopPropagation()}>
                                                <div className="HomePage__workspace-overlay-header">
                                                        <h3>Select a Workspace You Own</h3>
                                                        <button className="HomePage__workspace-overlay-close" onClick={handleCloseOwnedOverlay}>
                                                                <i className="fas fa-times"></i>
                                                        </button>
                                                </div>

                                                <div className="HomePage__workspace-list">
                                                        {ownedWorkspaces.map(workspace => (
                                                                <div
                                                                        key={workspace.id}
                                                                        className="HomePage__workspace-list-item"
                                                                        onClick={() => handleOwnedWorkspaceSelection(workspace)}
                                                                >
                                                                        <span>{workspace.name}</span>
                                                                        <span className="HomePage__workspace-role">OWNER</span>
                                                                </div>
                                                        ))}
                                                </div>
                                        </div>
                                </div>
                        )}

                        {/* What We Offer Modal */}
                        {showWhatWeOfferModal && (
                                <div className="HomePage__what-we-offer-overlay" onClick={handleCloseWhatWeOffer}>
                                        <div className="HomePage__what-we-offer-modal" onClick={e => e.stopPropagation()}>
                                                <div className="HomePage__what-we-offer-header">
                                                        <h2>What BBD Offers</h2>
                                                        <button className="HomePage__what-we-offer-close" onClick={handleCloseWhatWeOffer}>
                                                                <i className="fas fa-times"></i>
                                                        </button>
                                                </div>
                                                
                                                <div className="HomePage__what-we-offer-content">
                                                        <div className="HomePage__feature-grid">
                                                                {/* My Notebook */}
                                                                <div className="HomePage__feature-card">
                                                                        <div className="HomePage__feature-icon">
                                                                                <i className="fas fa-book"></i>
                                                                        </div>
                                                                        <h3>My Notebook</h3>
                                                                                <p>A virtual notebook for all your ideas, sketches, and notes. Access your notebook anywhere, anytime.</p>
                                                                                <ul>
                                                                                        <li>Customize page backgrounds</li>
                                                                                        <li>Access your notebook across any device</li>
                                                                                        <li>Auto-save and cloud sync</li>
                                                                                </ul>
                                                                </div>

                                                                {/* Personal Documents */}
                                                                <div className="HomePage__feature-card">
                                                                        <div className="HomePage__feature-icon">
                                                                                <i className="fas fa-file-alt"></i>
                                                                        </div>
                                                                        <h3>Personal Documents</h3>
                                                                        <p>Create and organize your own documentation with spaces, pages, and interactive walkthroughs.</p>
                                                                        <ul>
                                                                                <li>Spaces for organization</li>
                                                                                <li>Rich pages with media</li>
                                                                                <li>Step-by-step walkthroughs</li>
                                                                                <li>Public sharing options</li>
                                                                                <li>Analytics & insights</li>
                                                                        </ul>
                                                                </div>

                                                                {/* Team Workspaces */}
                                                                <div className="HomePage__feature-card">
                                                                        <div className="HomePage__feature-icon">
                                                                                <i className="fas fa-users"></i>
                                                                        </div>
                                                                        <h3>Team Workspaces</h3>
                                                                        <p>Collaborate with your team on shared documentation projects with advanced permissions and organization; with your very own domain.</p>
                                                                        <ul>
                                                                                <li>Team collaboration</li>
                                                                                <li>Role-based permissions</li>
                                                                                <li>Shared spaces & pages</li>
                                                                                <li>Team walkthroughs</li>
                                                                        </ul>
                                                                </div>

                                                                {/* Public Sharing */}
                                                                {/* <div className="HomePage__feature-card">
                                                                        <div className="HomePage__feature-icon">
                                                                                <i className="fas fa-share-alt"></i>
                                                                        </div>
                                                                        <h3>Public Sharing</h3>
                                                                        <p>Share your knowledge with the world. Make your documents publicly accessible with beautiful, clean layouts.</p>
                                                                        <ul>
                                                                                <li>Public documentation sites</li>
                                                                                <li>SEO-friendly pages</li>
                                                                                <li>Custom domain support</li>
                                                                                <li>Analytics & insights</li>
                                                                        </ul>
                                                                </div> */}
                                                        </div>

                                                        {/* Free Registration Assurance */}
                                                        <div className="HomePage__free-registration-section">
                                                                <div className="HomePage__free-registration-content">
                                                                        <i className="fas fa-shield-alt"></i>
                                                                        <div className="HomePage__free-registration-text">
                                                                                <h4>100% Free Forever</h4>
                                                                                <p>
                                                                                    All functionality is free. No hidden fees, no catches 
                                                                                    - just powerful documentation tools at your fingertips. Optionally 
                                                                                    pick up a paid storage plan to unlock themes, or subscribe for additional notebook backgrounds.
                                                                                </p>
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div className="HomePage__cta-section">
                                                                <h3>Ready to get started?</h3>
                                                                <div className="HomePage__cta-buttons">
                                                                        {loggedIn ? (
                                                                                <>
                                                                                        <button className="HomePage__cta-button primary" onClick={() => { navigate("/create"); handleCloseWhatWeOffer(); }}>
                                                                                                Create Document
                                                                                        </button>
                                                                                        <button className="HomePage__cta-button primary" onClick={() => { navigate("/walkthroughs/create"); handleCloseWhatWeOffer(); }}>
                                                                                                Create Walkthrough
                                                                                        </button>
                                                                                        <button className="HomePage__cta-button primary" onClick={() => { navigate("/workspaces"); handleCloseWhatWeOffer(); }}>
                                                                                                Your Workspace
                                                                                        </button>
                                                                                        <button className="HomePage__cta-button secondary" onClick={() => { navigate("/my-notebook"); handleCloseWhatWeOffer(); }}>
                                                                                                Try My Notebook
                                                                                        </button>
                                                                                </>
                                                                        ) : (
                                                                                <>
                                                                                        <button className="HomePage__cta-button primary" onClick={() => { navigate("/register"); handleCloseWhatWeOffer(); }}>
                                                                                                Create Account
                                                                                        </button>
                                                                                        <button className="HomePage__cta-button secondary" onClick={() => { navigate("/my-notebook"); handleCloseWhatWeOffer(); }}>
                                                                                                Try My Notebook
                                                                                        </button>
                                                                                </>
                                                                        )}
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        )}

                        {/* Download Modal */}
                        {showDownloadModal && (
                                <div className="HomePage__what-we-offer-overlay" onClick={handleCloseDownloadModal}>
                                        <div className="HomePage__what-we-offer-modal HomePage__download-modal" onClick={e => e.stopPropagation()}>
                                                <div className="HomePage__what-we-offer-header">
                                                        <h2>Download Bluebird Focus</h2>
                                                        <button className="HomePage__what-we-offer-close" onClick={handleCloseDownloadModal}>
                                                                <i className="fas fa-times"></i>
                                                        </button>
                                                </div>

                                                <div className="HomePage__what-we-offer-content">
                                                        <p className="HomePage__download-subtitle">
                                                                A minimal, distraction-free focus timer for your desktop.
                                                        </p>
                                                        <div className="HomePage__download-cards">
                                                                {/* Linux */}
                                                                <div className="HomePage__download-card">
                                                                        <div className="HomePage__download-card-icon">
                                                                                <i className="fab fa-linux"></i>
                                                                        </div>
                                                                        <div className="HomePage__download-card-info">
                                                                                <h3>Linux</h3>
                                                                                <p>v0.1.0 &middot; tar.gz &middot; AppImage included</p>
                                                                        </div>
                                                                        <a
                                                                                href="https://github.com/YomenT/bluebird-focus/releases/latest"
                                                                                className="HomePage__download-btn"
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                        >
                                                                                <i className="fas fa-download"></i>
                                                                                Download
                                                                        </a>
                                                                </div>

                                                                {/* Windows */}
                                                                <div className="HomePage__download-card">
                                                                        <div className="HomePage__download-card-icon">
                                                                                <i className="fab fa-windows"></i>
                                                                        </div>
                                                                        <div className="HomePage__download-card-info">
                                                                                <h3>Windows</h3>
                                                                                <p>v0.1.0 &middot; zip &middot; NSIS installer included</p>
                                                                        </div>
                                                                        <a
                                                                                href="https://github.com/YomenT/bluebird-focus/releases/latest"
                                                                                className="HomePage__download-btn"
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                        >
                                                                                <i className="fas fa-download"></i>
                                                                                Download
                                                                        </a>
                                                                </div>
                                                        </div>

                                                        <div className="HomePage__download-footer-note">
                                                                <i className="fab fa-github"></i>
                                                                <span>View source and all releases on <a href="https://github.com/YomenT/bluebird-focus" target="_blank" rel="noopener noreferrer">GitHub</a></span>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        )}

                        {enhancedRightClickEnabled && ContextMenuComponent}
                </div>
        );
}

export default HomePage